import React from 'react';

export interface ProgramTheme {
	main: string;
	label: string;
	surface: string;
}

export interface Program {
	number: number;
	title: string;
	/** GROUP_LABELS 인덱스 */
	groupIndex: number;
	theme: ProgramTheme;
	chip: string;
	detailLabel: string;
	detail: React.ReactNode[];
	stats: { label: string; value: string }[];
	notes: string[];
}

const PURPLE: ProgramTheme = { main: '#6400FF', label: '#6400FF', surface: '#F9F6FF' };
const ORANGE: ProgramTheme = { main: '#F57C00', label: '#FB8C00', surface: '#FFF3E0' };
const BLUE: ProgramTheme = { main: '#0B74E2', label: '#0B74E2', surface: '#F1F8FF' };

export const GROUP_LABELS: { text: string; width: number }[] = [
	{ text: '할당대상업체 전용', width: 684 },
	{ text: '할당대상업체 신청 불가', width: 330 },
	{ text: '할당대상업체 무관', width: 330 },
];

export const PROGRAMS: Program[] = [
	{
		number: 1,
		title: '탄소중립설비 지원사업',
		groupIndex: 0,
		theme: PURPLE,
		chip: '보조금 10억원 이상',
		detailLabel: '이런 기업에 해당합니다',
		detail: [
			<>
				국가 탄소중립 목표 달성을 위해 <strong>배출권거래제 할당대상업체</strong>가 탄소중립설비를 도입할 때 국고보조금을 지원하는 사업입니다.
			</>,
			<>
				감축설비에 필요한 보조금 규모가 <strong>10억원 이상</strong>인 경우 신청할 수 있고, 중소·중견·대기업은 물론 공공기관, 학교, 병원도 대상에 포함됩니다.
			</>,
		],
		stats: [
			{ label: '보조율', value: '30~70%' },
			{ label: '지원한도', value: '사업장 60억 · 업체 100억' },
		],
		notes: ['태양광은 사업장에서 실제로 쓰는 전력량 범위 안에서 지원되고, 생산한 전력은 사업장에서 먼저 사용해야 합니다. 부가세와 건물공사비, 컨설팅비는 지원 대상이 아닙니다.'],
	},
	{
		number: 2,
		title: '배출권거래제\n감축설비 지원사업',
		groupIndex: 0,
		theme: PURPLE,
		chip: '보조금 10억원 미만',
		detailLabel: '이런 기업에 해당합니다',
		detail: [
			'할당대상업체의 온실가스 감축을 위해 감축효과가 우수한 설비의 보급을 지원하는 사업으로, 한국에너지공단이 운영합니다.',
			'배출권거래제 할당대상업체이면서 보조금 규모가 10억원 미만인 경우 신청할 수 있고, 중소·중견기업만 대상이라 대기업·공공기관·학교·병원은 제외됩니다.',
		],
		stats: [
			{ label: '보조율', value: '중소 70% · 중견 50% 이내' },
			{ label: '지원한도', value: '업체별 30억' },
		],
		notes: [
			'고효율 인버터·펌프·냉동기, 공기압축기 시스템, 폐열회수 설비, 연료전환 등 감축효과가 확인된 설비가 대상입니다.',
			'단순 LED 조명 교체는 제외되고, 지원받은 설비는 설치 후 5년간 유지해야 합니다.',
		],
	},
	{
		number: 3,
		title: '스마트 생태공장\n구축사업',
		groupIndex: 1,
		theme: ORANGE,
		chip: '제조공장 보유 기업',
		detailLabel: '이런 기업에 해당합니다',
		detail: [
			'오염물질 배출 비중이 높은 제조공장을 소유한 중소·중견기업을 대상으로, 온실가스·오염물질 저감과 에너지·자원 효율 제고를 고려한 친환경 공장 전환·구축을 지원하는 사업입니다(한국환경공단 운영).',
			'국내 제조공장을 소유한 중소·중견기업이 신청할 수 있고, 배출권거래제 할당대상업체는 신청이 제한됩니다.',
		],
		stats: [
			{ label: '보조율', value: '중소 60% 이하 · 중견 50% 이하' },
			{ label: '지원규모', value: '기업당 최대 10억' },
		],
		notes: [
			'지원분야는 온실가스 저감, 대기오염 저감, 수질오염 저감, 폐기물배출 저감, 자원순환, 환경보건, 기타 시설의 7개입니다.',
			'이 중 온실가스 저감 분야를 반드시 포함해 2개 분야 이상을 선택해 신청해야 하고, 시설 1개당 지원분야는 1개만 적용됩니다.',
		],
	},
	{
		number: 4,
		title: '건물지원사업',
		groupIndex: 2,
		theme: BLUE,
		chip: '건물·시설물 소유자',
		detailLabel: '이런 기업(건물 소유자)에 해당합니다',
		detail: [
			'「신·재생에너지 설비의 지원 등에 관한 규정」에 따라 건물·시설물에 태양광, 태양열, 지열 등 재생에너지 설비 설치비의 일부를 보조금으로 지원하는 사업입니다(기후에너지환경부 공고, 한국에너지공단 신재생에너지센터 접수).',
			'주택을 제외한 건물·시설물 소유자라면 할당대상업체 여부와 무관하게 신청 가능.',
			'사립학교, 전통시장도 별도 트랙으로 포함되며, 자가소비 목적의 신규 발전설비 설치만 지원합니다.',
		],
		stats: [
			{ label: '보조율', value: '중소 60% 이하 · 중견 50% 이하' },
			{ label: '지원규모', value: '기업당 최대 10억' },
		],
		notes: [
			'발전된 잉여 전기는 거래·판매할 수 없고, 태양광은 저탄소 모듈(탄소배출량 655kg·CO₂-eq/kW 이하)과 정격효율 17.5% 이상 인증제품만 지원됩니다.',
			'지자체 조례 등에 따른 재생에너지 설치의무 대상 건물은 제외되며, 의무비율을 충족한 건물의 추가 설치분은 지원 가능합니다.',
		],
	},
];
